// ======================================================================
// File Name       : Pagination
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Pagination } from 'react-bootstrap';
import { RETURN_MAX_NUM } from '../constants/index';

/**
 * Pagination
 * props: recordNum (total record)
 * props: handlePageSelect (return startPosition by page selected)
 */
class PaginationCustom extends Component {
  constructor() {
    super();
    this.handlePageSelect = this.handlePageSelect.bind(this);
  }

  handlePageSelect(pageNum) {
    const { returnMaxNum } = this.props;
    let { startPosition } = this.props;
    const activePage = Math.ceil(startPosition / returnMaxNum) + 1;
    if (activePage !== pageNum) {
      startPosition = (pageNum - 1) * returnMaxNum;
      this.props.handlePageSelect(startPosition);
    }
  }

  renderPageItems(totalPages, activePage) {
    const maxButtons = 4;

    if (totalPages <= maxButtons + 1) {
      return Array.from({ length: totalPages }, (_, index) => {
        const pageNum = index + 1;

        return (
          <Pagination.Item
            key={pageNum}
            active={pageNum === activePage}
            onClick={() => this.handlePageSelect(pageNum)}
          >
            {pageNum}
          </Pagination.Item>
        );
      });
    }

    const pageItems = [];

    let startPage = activePage - Math.floor(maxButtons / 2);
    let endPage = startPage + maxButtons - 1;

    if (startPage < 2) {
      startPage = 2;
      endPage = startPage + maxButtons - 1;
    }

    if (endPage > totalPages - 1) {
      endPage = totalPages - 1;
      startPage = endPage - maxButtons + 1;
    }

    // First page
    pageItems.push(
      <Pagination.Item
        key={1}
        active={activePage === 1}
        onClick={() => this.handlePageSelect(1)}
      >
        1
      </Pagination.Item>
    );

    // Left ellipsis
    if (startPage > 2) {
      pageItems.push(<Pagination.Ellipsis key="ellipsis-left" />);
    }

    // Middle pages
    for (let pageNum = startPage; pageNum <= endPage; pageNum += 1) {
      pageItems.push(
        <Pagination.Item
          key={pageNum}
          active={pageNum === activePage}
          onClick={() => this.handlePageSelect(pageNum)}
        >
          {pageNum}
        </Pagination.Item>
      );
    }

    // Right ellipsis
    if (endPage < totalPages - 1) {
      pageItems.push(<Pagination.Ellipsis key="ellipsis-right" />);
    }

    // Last page
    pageItems.push(
      <Pagination.Item
        key={totalPages}
        active={activePage === totalPages}
        onClick={() => this.handlePageSelect(totalPages)}
      >
        {totalPages}
      </Pagination.Item>
    );

    return pageItems;
  }

  render() {
    const {
      className,
      returnMaxNum = RETURN_MAX_NUM,
      startPosition,
      recordNum,
    } = this.props;

    const items = Math.ceil(recordNum / returnMaxNum);

    if (items === 0) {
      return (
        <Pagination className={className} size="sm">
          <Pagination.First disabled />
          <Pagination.Prev disabled />
          <Pagination.Next disabled />
          <Pagination.Last disabled />
        </Pagination>
      );
    }

    let activePage = Math.ceil(startPosition / returnMaxNum) + 1;

    if (activePage > items) {
      activePage = 1;
    }

    return (
      <Pagination className={className} size="sm">
        <Pagination.First
          disabled={activePage === 1}
          onClick={() => this.handlePageSelect(1)}
        />

        <Pagination.Prev
          disabled={activePage === 1}
          onClick={() => this.handlePageSelect(activePage - 1)}
        />

        {this.renderPageItems(items, activePage)}

        <Pagination.Next
          disabled={activePage === items}
          onClick={() => this.handlePageSelect(activePage + 1)}
        />

        <Pagination.Last
          disabled={activePage === items}
          onClick={() => this.handlePageSelect(items)}
        />
      </Pagination>
    );
  }
}

PaginationCustom.propTypes = {
  className: PropTypes.string,
  handlePageSelect: PropTypes.func,
  returnMaxNum: PropTypes.number,
  startPosition: PropTypes.number,
  recordNum: PropTypes.number,
};

PaginationCustom.defaultProps = {
  className: '',
  handlePageSelect: () => false,
  returnMaxNum: RETURN_MAX_NUM,
  startPosition: 0,
  recordNum: 0,
};

export default PaginationCustom;
